// src/components/EmailVerificationBanner.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import styles from '../styles/EmailVerificationBanner.module.css';

const EmailVerificationBanner = () => {
  const { currentUser, sendVerificationEmail } = useAuth();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showBanner, setShowBanner] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  // Effect to refresh user verification status
  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (currentUser) {
        try {
          // Reload the user to get the latest verification status
          await currentUser.reload();
          setIsVerified(auth.currentUser.emailVerified);
        } catch (error) {
          console.error("Error refreshing user data:", error);
        }
      }
    };
    
    checkVerificationStatus();
    
    // Check status every 10 seconds in case user verified in another tab
    const interval = setInterval(checkVerificationStatus, 10000);
    return () => clearInterval(interval);
  }, [currentUser]);

  // If user is not logged in or already verified, don't show banner
  if (!currentUser || isVerified || currentUser.emailVerified || !showBanner) {
    return null;
  }

  const handleResendVerification = async () => {
    try {
      setError('');
      setMessage('');
      setSending(true);
      
      await sendVerificationEmail();
      
      setMessage('Verification email has been sent. Please check your inbox.');
      
      // Auto-hide message after 5 seconds
      setTimeout(() => {
        setMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError('Failed to send verification email. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.bannerWrapper}>
      <div className={styles.banner}>
        <div className={styles.content}>
          <div className={styles.icon}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </div>
          <div className={styles.message}>
            <strong>Please verify your email address</strong>
            <p>To protect your account and enable all features, please check your inbox for a verification link.</p>
          </div>
          <div className={styles.actions}>
            <button 
              className={styles.verifyButton} 
              onClick={handleResendVerification}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Resend Email'}
            </button>
            <button 
              className={styles.closeButton} 
              onClick={() => setShowBanner(false)}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {message && <div className={styles.success}>{message}</div>}
      </div>
    </div>
  );
};

export default EmailVerificationBanner;